/**
 * miseユーザー一括登録CSV関連ヘルパー
 */
import Papa from 'papaparse';
import { downloadCsvWithBom } from '@/lib/utils';
import type { BulkCreateUserRowResult, ParsedBulkUserRow } from '@/lib/types/mise/user';

const REQUIRED_COLUMNS = ['user_email', 'seller_id'] as const;

/**
 * テンプレートCSV文字列を生成
 */
export function generateBulkUserTemplateCsv(): string {
  return 'user_email,seller_id,user_name\nfoo@example.com,seller_001,店舗A\nbar@example.com,seller_002,\n';
}

/**
 * CSV文字列をBOM付きUTF-8でダウンロードさせる(既存ヘルパーへの薄いラッパー)
 */
export function downloadBulkUserCsv(filename: string, csvContent: string): void {
  // BOMを付与した上で既存の共通ダウンローダーを利用する。
  // 既存ヘルパーは「BOM付き済み」の文字列を期待するため、ここでBOMを先頭に付ける。
  const bom = '﻿';
  downloadCsvWithBom(bom + csvContent, filename);
}

export interface ParseBulkUserCsvResult {
  rows: ParsedBulkUserRow[];
  errors: string[];
}

/**
 * 入力CSVをパースして BulkCreateUserRow 配列を返す
 * - ヘッダー必須: user_email, seller_id
 * - user_name は任意。列が無い・空欄の行はメールアドレスのローカル部を使う
 * - 余分な列は無視
 * - 空行はスキップ
 */
export function parseBulkUserCsv(csvText: string): ParseBulkUserCsvResult {
  const errors: string[] = [];
  const result = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h) => h.trim(),
  });

  if (result.errors.length > 0) {
    for (const e of result.errors) {
      errors.push(
        `CSV解析エラー: ${e.message} (行 ${typeof e.row === 'number' ? e.row + 1 : '?'})`,
      );
    }
  }

  const fields = (result.meta.fields ?? []).map((f) => f.trim());
  for (const required of REQUIRED_COLUMNS) {
    if (!fields.includes(required)) {
      errors.push(`必須列 '${required}' が見つかりません`);
    }
  }
  if (errors.length > 0) {
    return { rows: [], errors };
  }

  const rows: ParsedBulkUserRow[] = (result.data as Record<string, string>[]).map((raw, index) => {
    const userEmail = (raw.user_email ?? '').trim();
    const userName = (raw.user_name ?? '').trim();
    return {
      row_index: index,
      user_email: userEmail,
      seller_id: (raw.seller_id ?? '').trim(),
      user_name: userName || deriveUserNameFromEmail(userEmail),
    };
  });

  return { rows, errors };
}

/**
 * メールアドレスのローカル部から user_name を生成
 */
export function deriveUserNameFromEmail(email: string): string {
  const trimmed = email.trim();
  const atIndex = trimmed.indexOf('@');
  if (atIndex <= 0) {
    return trimmed;
  }
  return trimmed.substring(0, atIndex);
}

/**
 * 結果一覧から結果CSV文字列を生成
 * 列: row_index, user_email, user_name, seller_id, status, temporary_password, error
 *
 * row_index は CSV 閲覧時に分かりやすいよう 1 始まりで出力する
 * (内部表現は 0 始まり)
 */
export function generateBulkCreateResultCsv(results: BulkCreateUserRowResult[]): string {
  return Papa.unparse(
    {
      fields: [
        'row_index',
        'user_email',
        'user_name',
        'seller_id',
        'status',
        'temporary_password',
        'error',
      ],
      data: results.map((r) => [
        r.row_index + 1,
        r.user_email,
        r.user_name,
        r.seller_id,
        r.status,
        r.temporary_password ?? '',
        r.error ?? '',
      ]),
    },
    { newline: '\n' },
  );
}
